/**
 * Online data layer (VITE_API_MODE=http): the same functions as ./mock.ts, backed by the LOVE CPMD API.
 * The signed-in member comes from the login cookie, so the `actorId` / `userId` arguments the pages pass
 * are only kept for the shared signatures — the server never trusts them.
 */
import type {
  ActivityEntry,
  AppNotification,
  Category,
  FillPreviewItem,
  GuestPresenter,
  ISODate,
  Member,
  MemberInput,
  NotificationPrefs,
  PresenterRef,
  ProfileInput,
  Rotation,
  ScheduleInput,
  Session,
  Settings,
  Summary,
  SummaryInput,
  TopicInput,
} from "@love-cpmd/core";
import { ApiError, type ApiErrorCode } from "./errors";

const BASE = "/api";

/** Fired when the server says the login is no longer valid; the auth provider then shows the login page. */
export const UNAUTHORIZED_EVENT = "love-cpmd:unauthorized";

const STATUS_CODE: Record<number, ApiErrorCode> = {
  400: "VALIDATION",
  401: "AUTH",
  403: "FORBIDDEN",
  404: "NOT_FOUND",
  409: "CONFLICT",
  413: "VALIDATION",
  429: "RATE_LIMIT",
};

function fallbackMessage(status: number): string {
  if (status === 401) return "Your session has ended. Please log in again.";
  if (status === 403) return "You don't have access to do this.";
  if (status === 429) return "Too many attempts. Wait a minute and try again.";
  if (status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 530)) return "The server is waking up. Try again in a moment.";
  return "Something went wrong. Please try again.";
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
  try {
    return await fetch(BASE + path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? { accept: "application/json" } : { accept: "application/json", "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("NETWORK", "Can't reach the LOVE CPMD server. Check your connection and try again.");
  }
}

/** A successful answer that isn't JSON comes from the hosting (e.g. a "starting up" page), not from the API. */
function assertApiAnswer(res: Response): void {
  if (!res.ok || res.status === 204) return;
  if (!(res.headers.get("content-type") ?? "").includes("json")) throw new ApiError("SERVER", fallbackMessage(503));
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await send(method, path, body);
  if (res.status === 204) return undefined as T;
  assertApiAnswer(res);
  const data = (await readJson(res)) as { error?: { code?: ApiErrorCode; message?: string; fields?: Record<string, string>; meta?: Record<string, unknown> } } | null;
  if (!res.ok) {
    const err = data?.error;
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(err?.code ?? STATUS_CODE[res.status] ?? "SERVER", err?.message ?? fallbackMessage(res.status), err?.fields, err?.meta);
  }
  return data as T;
}

/* ─────────────────────────── auth ─────────────────────────── */

/** Better Auth sign-in with the Member ID as username; the session cookie is set by the server. */
export async function login(memberId: string, password: string, remember = true): Promise<Member> {
  const res = await send("POST", "/auth/sign-in/username", { username: memberId.trim(), password, rememberMe: remember });
  if (!res.ok) {
    if (res.status === 429) throw new ApiError("RATE_LIMIT", "Too many attempts. Wait a minute and try again.");
    if (res.status >= 500) {
      const err = ((await readJson(res)) as { error?: { message?: string } } | null)?.error;
      throw new ApiError("SERVER", err?.message ?? fallbackMessage(res.status));
    }
    // 403 = the server refused this website (address or proxy secret settings), not a wrong password.
    if (res.status === 403) throw new ApiError("FORBIDDEN", "Sign-in is blocked by the server settings. Contact the System Manager.");
    // Same message for an unknown ID, a wrong password or a deactivated account.
    throw new ApiError("AUTH", "Incorrect Member ID or password.");
  }
  assertApiAnswer(res);
  return request<Member>("GET", "/me");
}

/** The member behind the current cookie, or null when signed out. */
export async function currentUser(): Promise<Member | null> {
  const res = await send("GET", "/me");
  if (res.status === 401) return null;
  assertApiAnswer(res);
  const data = await readJson(res);
  if (!res.ok) {
    const err = (data as { error?: { code?: ApiErrorCode; message?: string } } | null)?.error;
    throw new ApiError(err?.code ?? STATUS_CODE[res.status] ?? "SERVER", err?.message ?? fallbackMessage(res.status));
  }
  return data as Member;
}

export async function signOut(): Promise<void> {
  await send("POST", "/auth/sign-out", {}).catch(() => undefined);
}

export async function changePassword(_userId: string, current: string, next: string): Promise<void> {
  await request<void>("POST", "/me/password", { current, next });
}

/* ─────────────────────────── reads ─────────────────────────── */

export const getMembers = () => request<Member[]>("GET", "/members");
export const getGuestPresenters = () => request<GuestPresenter[]>("GET", "/guest-presenters");
export const getCategories = () => request<Category[]>("GET", "/categories");
export const getSessions = () => request<Session[]>("GET", "/sessions");
export const getSummaries = () => request<Summary[]>("GET", "/summaries");
export const getRotations = () => request<Rotation[]>("GET", "/rotations");
export const getActivity = () => request<ActivityEntry[]>("GET", "/activity");
export const getSettings = () => request<Settings>("GET", "/settings");
export const getNotifications = (_userId: string) => request<AppNotification[]>("GET", "/notifications");
export const getPrefs = (_userId: string) => request<NotificationPrefs>("GET", "/me/prefs");

export async function recordSummaryView(summaryId: string): Promise<void> {
  await request<void>("POST", `/summaries/${encodeURIComponent(summaryId)}/view`).catch(() => undefined);
}

/* ─────────────────────────── schedule ─────────────────────────── */

const sessionPath = (id: string, action: string) => `/sessions/${encodeURIComponent(id)}/${action}`;

export const createSchedule = (_actorId: string, input: ScheduleInput) => request<Session>("POST", "/sessions", input);

export const updateTopic = (_actorId: string, sessionId: string, input: TopicInput) => request<Session>("PATCH", sessionPath(sessionId, "topic"), input);

export const reschedule = (_actorId: string, sessionId: string, newDate: ISODate, reason = "") =>
  request<Session>("POST", sessionPath(sessionId, "reschedule"), { date: newDate, reason });

export const swapSchedules = (_actorId: string, sessionId: string, otherId: string, expected: { a: ISODate; b: ISODate }, reason = "") =>
  request<Session>("POST", sessionPath(sessionId, "swap"), { otherId, expected, reason });

export const markDone = (_actorId: string, sessionId: string, actual?: PresenterRef) => request<Session>("POST", sessionPath(sessionId, "done"), { actual });

export const assignReplacement = (_actorId: string, sessionId: string, replacement: PresenterRef) =>
  request<Session>("POST", sessionPath(sessionId, "replacement"), { presenter: replacement });

export const cancelSession = (_actorId: string, sessionId: string, reason: string) => request<Session>("POST", sessionPath(sessionId, "cancel"), { reason });

export const logSpontaneous = (_actorId: string, parentId: string, input: { presenter: PresenterRef | null; categoryId: string; topic: string }) =>
  request<Session>("POST", sessionPath(parentId, "spontaneous"), input);

export async function fillFromRotation(_actorId: string, items: FillPreviewItem[]): Promise<number> {
  const result = await request<{ count: number }>("POST", "/rotations/current/fill", { items });
  return result.count;
}

/* ─────────────────────────── summaries ─────────────────────────── */

export const saveSummary = (_actorId: string, sessionId: string, input: SummaryInput, options: { baseVersion: number | null; force?: boolean }) =>
  request<Summary>("PUT", sessionPath(sessionId, "summary"), { input, baseVersion: options.baseVersion, force: options.force });

/* ─────────────────────────── rotation ─────────────────────────── */

export const generateRotation = (_actorId: string) => request<Rotation>("POST", "/rotations/generate");
export const reorderRotation = (_actorId: string, order: string[]) => request<Rotation>("PUT", "/rotations/current/order", { order });
export const lockRotation = (_actorId: string) => request<Rotation>("POST", "/rotations/current/lock");
export const unlockRotation = (_actorId: string) => request<Rotation>("POST", "/rotations/current/unlock");

/* ─────────────────────────── notifications ─────────────────────────── */

export async function markNotificationRead(_userId: string, id: string): Promise<void> {
  await request<void>("POST", `/notifications/${encodeURIComponent(id)}/read`);
}

export async function markAllNotificationsRead(_userId: string): Promise<void> {
  await request<void>("POST", "/notifications/read-all");
}

/** Creates H-7 / H-1 / D-day reminders that are due (the server does the work). */
export async function syncReminders(_userId: string): Promise<number> {
  try {
    const result = await request<{ created: number }>("POST", "/notifications/sync");
    return result.created;
  } catch {
    return 0;
  }
}

/* ─────────────────────────── profile ─────────────────────────── */

export const updateProfile = (_userId: string, input: ProfileInput) => request<Member>("PATCH", "/me", input);

export async function updateAvatar(_userId: string, avatarUrl: string | null): Promise<void> {
  await request<void>("PUT", "/me/avatar", { avatarUrl });
}

export async function updatePrefs(_userId: string, prefs: NotificationPrefs): Promise<void> {
  await request<void>("PUT", "/me/prefs", prefs);
}

/* ─────────────────────────── manage system ─────────────────────────── */

export const addMember = (_actorId: string, input: MemberInput) => request<{ member: Member; password: string }>("POST", "/admin/members", input);

export const updateMember = (_actorId: string, id: string, patch: Partial<Pick<Member, "role" | "active" | "name" | "nickname">>) =>
  request<Member>("PATCH", `/admin/members/${encodeURIComponent(id)}`, patch);

export async function resetPassword(_actorId: string, id: string): Promise<string> {
  const result = await request<{ password: string }>("POST", `/admin/members/${encodeURIComponent(id)}/reset-password`);
  return result.password;
}

export const addCategory = (_actorId: string, name: string) => request<Category>("POST", "/admin/categories", { name });

export const updateCategory = (_actorId: string, id: string, patch: Partial<Pick<Category, "name" | "active">>) =>
  request<Category>("PATCH", `/admin/categories/${encodeURIComponent(id)}`, patch);

export const addGuestPresenter = (_actorId: string, name: string, note: string) => request<GuestPresenter>("POST", "/admin/guest-presenters", { name, note });

export async function updateGuestPresenter(_actorId: string, id: string, patch: Partial<Pick<GuestPresenter, "active" | "name" | "note">>): Promise<void> {
  await request<void>("PATCH", `/admin/guest-presenters/${encodeURIComponent(id)}`, patch);
}

export const updateSettings = (_actorId: string, settings: Settings) => request<Settings>("PUT", "/admin/settings", settings);

export async function resetData(_actorId: string, mode: "sample" | "empty"): Promise<void> {
  await request<void>("POST", "/admin/data/reset", { mode });
}

/** JSON snapshot of everything (passwords are never included). */
export async function exportData(_actorId: string): Promise<string> {
  const data = await request<unknown>("GET", "/admin/data/export");
  return JSON.stringify(data, null, 2);
}
